import { Draggable, Droppable } from '@hello-pangea/dnd';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { coversApi } from '@/api/covers';
import { CoverThumbnail } from '@/components/cover/CoverThumbnail';
import { COVER_TEMPLATES } from '@/components/cover/templates/registry';
import {
    ArrowLeft2,
    Danger,
    Tick,
    Edit2,
    GripVertical,
    Add,
    Image,
    Trash,
} from '@/components/icons';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HoverIntentGroup } from '@/components/ui/hover-intent-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SECTIONS } from '@/components/wizard/constants';
import { useAuth } from '@/context/AuthContext';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { ReportEditorAction } from '@/lib/analytics-events';
import { capture } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import { useReportWizard } from '../context/ReportWizardContext';
import { CONCLUDED_STATUS, PENDING_STATUS, TO_DO_STATUS } from './constants';
import type { CoverFieldContext } from '@/components/cover/resolve-cover-fields';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';

interface StepsSidebarProps {
    isSidebarExpanded: boolean;
    setIsLeftSidebarCollapsed: (collapsed: boolean) => void;
    isInSheet?: boolean;
    onClose?: () => void;
    onStepSelect?: () => void;
}

export const StepsSidebar: React.FC<StepsSidebarProps> = ({
    isSidebarExpanded,
    setIsLeftSidebarCollapsed,
    isInSheet = false,
    onClose,
    onStepSelect,
}) => {
    const {
        currentStepIndex,
        showWarnings,
        handleStepClick,
        toggleManualComplete,
        isStepComplete,
        pendingConsolidations,
        pendingPublisherResultApprovalImports,
        sidebarSteps,
        handleAddCustomSection,
        handleDeleteCustomSection,
        isReadOnly,
        formData,
    } = useReportWizard();
    const customSectionsAccess = useFeatureEntitlement('reports.customSections');
    const coverAccess = useFeatureEntitlement('reports.cover');
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const isCoverView = searchParams.get('view') === 'capa';
    const reportCover = formData.customizations?.cover;
    const coverFieldContext: CoverFieldContext = {
        patient: formData.patient,
        finalidade: formData.finalidade,
        psychologist: user
            ? {
                  full_name: user.full_name,
                  crp: user.crp,
                  city: user.city,
                  state: user.state,
              }
            : undefined,
    };
    const isCoverBlocked = coverAccess.shouldBlock;
    const coverPlan = coverAccess.availableFromPlan;

    const { data: covers = [] } = useQuery({
        queryKey: ['covers'],
        queryFn: coversApi.list,
        enabled: Boolean(reportCover?.cover_id) && !isCoverBlocked,
    });

    const coverLabel = reportCover
        ? (covers.find((c) => c.id === reportCover.cover_id)?.name ??
          COVER_TEMPLATES[reportCover.template_key]?.name ??
          'Configurada')
        : 'Não configurada';

    const pendingTestsCount =
        pendingConsolidations.length + pendingPublisherResultApprovalImports.length;
    const isCustomSectionBlocked = customSectionsAccess.shouldBlock;
    const customSectionPlan = customSectionsAccess.availableFromPlan;
    const customSectionTooltip = customSectionPlan
        ? `Seções personalizadas estão disponíveis no plano ${customSectionPlan.name}.`
        : 'Seu plano atual não permite adicionar seções personalizadas.';

    const handleClose = () => {
        if (onClose) {
            onClose();
            return;
        }

        setIsLeftSidebarCollapsed(true);
    };

    const handleSelectStep = (index: number) => {
        if (isCoverView) {
            setSearchParams(
                (params) => {
                    params.delete('view');
                    return params;
                },
                { replace: true },
            );
        }
        handleStepClick(index);
        onStepSelect?.();
    };

    const trackAction = (action: ReportEditorAction) => {
        capture('report_editor_action_clicked', { action, is_read_only: isReadOnly });
    };

    // Sem a capa no plano a aba continua aberta: a view vira prévia (ver CoverView).
    const openCoverView = () => {
        if (isCoverView) return;

        trackAction('cover_open');
        setSearchParams((params) => {
            params.set('view', 'capa');
            return params;
        });
        onStepSelect?.();
    };

    const renderStepCard = (
        step: (typeof sidebarSteps)[number],
        index: number,
        dragHandleProps?: DraggableProvidedDragHandleProps | null,
    ) => {
        const isActive = !isCoverView && currentStepIndex === index;
        const completed = isStepComplete(step.id);
        const showWarning = showWarnings && !completed && step.required !== false;
        const showPendingTestsBadge = step.id === SECTIONS.TESTS && pendingTestsCount > 0;

        return (
            <HoverIntentGroup
                className={cn(
                    'group/step relative mb-2 flex w-full items-center gap-2 rounded-lg p-3 transition-all',
                    isActive
                        ? 'bg-primary shadow-md'
                        : 'border border-border bg-surface hover:bg-surface-subtle',
                )}
            >
                {dragHandleProps ? (
                    <TooltipProvider delayDuration={400}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    {...dragHandleProps}
                                    className={cn(
                                        'absolute inset-y-0 -left-2 z-10 flex w-5 cursor-grab items-center justify-center rounded-l-lg opacity-0 transition-all focus:opacity-100 active:cursor-grabbing group-data-[hover-intent=on]/step:opacity-100',
                                        isActive
                                            ? 'text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground'
                                            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                                    )}
                                    aria-label="Reordenar seção"
                                >
                                    <GripVertical className="size-4" />
                                </button>
                            </TooltipTrigger>
                            {/* Above, not beside: the sidebar hugs the left
                                viewport edge, so a side tooltip lands on top of
                                the very card it is describing. */}
                            <TooltipContent side="top" align="start">
                                Arraste para reordenar a seção
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : null}

                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        trackAction(completed ? 'step_marked_todo' : 'step_marked_done');
                        toggleManualComplete(step.id, e);
                    }}
                    className={`flex size-8 flex-shrink-0 items-center justify-center rounded-full transition-all hover:scale-105 ${
                        showWarning
                            ? 'bg-[#F59E0B] text-white'
                            : completed
                              ? 'bg-[#10B981] text-white'
                              : isActive
                                ? 'bg-surface text-brand'
                                : 'border-2 border-border-strong bg-card text-muted-foreground'
                    }`}
                >
                    {showWarning ? (
                        <Danger className="size-4" />
                    ) : completed ? (
                        <Tick className="size-4" />
                    ) : isActive ? (
                        <Edit2 className="size-4" />
                    ) : null}
                </button>

                <button
                    type="button"
                    onClick={() => handleSelectStep(index)}
                    className="min-w-0 flex-1 text-left"
                >
                    <div
                        className={`mb-0.5 text-[10px] ${
                            isActive
                                ? 'text-primary-foreground/70'
                                : showWarning
                                  ? 'text-[#F59E0B]'
                                  : completed
                                    ? 'text-[#10B981]'
                                    : 'text-muted-foreground'
                        }`}
                    >
                        {showWarning ? PENDING_STATUS : completed ? CONCLUDED_STATUS : TO_DO_STATUS}
                    </div>
                    <div className="flex min-w-0 items-center gap-2">
                        <div
                            className={`truncate text-sm font-semibold ${
                                isActive ? 'text-primary-foreground' : 'text-foreground'
                            }`}
                        >
                            {step.title}
                        </div>
                        {showPendingTestsBadge && (
                            <Badge
                                variant={isActive ? 'outline' : 'warning'}
                                className={`shrink-0 px-1.5 py-0 text-[10px] leading-4 ${
                                    isActive
                                        ? 'border-primary-foreground/40 bg-primary-foreground text-primary'
                                        : ''
                                }`}
                            >
                                {pendingTestsCount > 9 ? '9+' : pendingTestsCount}
                            </Badge>
                        )}
                    </div>
                </button>

                {step.isCustom && !isReadOnly && (
                    <button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            // The click, not the confirm: reaching for delete is
                            // the intent, whether or not they go through with it.
                            trackAction('custom_section_remove');
                            handleDeleteCustomSection(step.id);
                        }}
                        className={cn(
                            'flex size-8 flex-shrink-0 items-center justify-center rounded transition-colors',
                            isActive
                                ? 'text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground'
                                : 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
                        )}
                        aria-label="Remover seção personalizada"
                    >
                        <Trash className="size-3.5" />
                    </button>
                )}
            </HoverIntentGroup>
        );
    };

    const [patientStep, ...bodySteps] = sidebarSteps;

    return (
        <div
            id={isSidebarExpanded ? 'tour-steps-sidebar' : undefined}
            className={cn(
                'relative flex flex-shrink-0 flex-col overflow-hidden bg-background transition-[width,border-color] duration-300',
                isSidebarExpanded
                    ? isInSheet
                        ? 'size-full border-r-0'
                        : 'w-[280px] border-r border-border'
                    : 'w-0 border-r border-transparent',
            )}
        >
            <div
                className={`flex h-full flex-col transition-opacity duration-200 ${
                    isSidebarExpanded ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
            >
                <div className="flex justify-end p-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={handleClose}
                        className="size-6 p-0"
                        title="Ocultar painel"
                    >
                        <ArrowLeft2 className="size-4" />
                    </Button>
                </div>
                <div className="flex-1 overflow-y-auto px-2 pb-4">
                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="cover-tab"
                                    onClick={openCoverView}
                                    className={cn(
                                        'group relative mb-2 flex w-full items-center gap-2 rounded-lg p-3 text-left transition-all',
                                        isCoverView
                                            ? 'bg-primary shadow-md'
                                            : 'border border-border bg-surface hover:bg-surface-subtle',
                                    )}
                                >
                                    {reportCover ? (
                                        <span
                                            className={cn(
                                                'flex-shrink-0 overflow-hidden rounded border bg-white',
                                                isCoverView
                                                    ? 'border-primary-foreground/30'
                                                    : 'border-border',
                                            )}
                                        >
                                            <CoverThumbnail
                                                width={22}
                                                cover={reportCover}
                                                context={coverFieldContext}
                                                customizations={formData.customizations}
                                            />
                                        </span>
                                    ) : (
                                        <span
                                            className={cn(
                                                'flex h-8 w-[22px] flex-shrink-0 items-center justify-center rounded border border-dashed',
                                                isCoverView
                                                    ? 'border-primary-foreground/40 text-primary-foreground/70'
                                                    : 'border-border-strong text-muted-foreground',
                                            )}
                                        >
                                            <Image className="size-4" />
                                        </span>
                                    )}
                                    <span className="min-w-0 flex-1">
                                        <span
                                            className={cn(
                                                'mb-0.5 block truncate text-[10px]',
                                                isCoverView
                                                    ? 'text-primary-foreground/70'
                                                    : 'text-muted-foreground',
                                            )}
                                        >
                                            {coverLabel}
                                        </span>
                                        <span
                                            className={cn(
                                                'block truncate text-sm font-semibold',
                                                isCoverView
                                                    ? 'text-primary-foreground'
                                                    : 'text-foreground',
                                            )}
                                        >
                                            Capa
                                        </span>
                                    </span>
                                    {isCoverBlocked && coverPlan ? (
                                        <span className="inline-flex shrink-0 rounded-full border border-brand/15 bg-brand-subtle px-2.5 py-1 text-[0.7rem] font-semibold leading-none text-brand">
                                            Plano+
                                        </span>
                                    ) : null}
                                </button>
                            </TooltipTrigger>
                            {isCoverBlocked && coverPlan ? (
                                <TooltipContent
                                    side="right"
                                    className="max-w-56 text-center leading-5"
                                >
                                    {`Veja as capas — disponíveis no plano ${coverPlan.name}.`}
                                </TooltipContent>
                            ) : null}
                        </Tooltip>
                    </TooltipProvider>

                    <div className="mb-2 mt-3 px-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Seções
                    </div>

                    {patientStep && renderStepCard(patientStep, 0)}

                    <Droppable
                        droppableId="report_sections"
                        type="report-section"
                        isDropDisabled={isReadOnly}
                    >
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                {bodySteps.map((step, index) => {
                                    const sidebarIndex = index + 1;

                                    return (
                                        <Draggable
                                            key={step.id}
                                            draggableId={`report-section-${step.id}`}
                                            index={index}
                                            isDragDisabled={isReadOnly || !step.isCustom}
                                        >
                                            {(draggableProvided) => (
                                                <div
                                                    ref={draggableProvided.innerRef}
                                                    {...draggableProvided.draggableProps}
                                                    style={draggableProvided.draggableProps.style}
                                                >
                                                    {renderStepCard(
                                                        step,
                                                        sidebarIndex,
                                                        step.isCustom && !isReadOnly
                                                            ? draggableProvided.dragHandleProps
                                                            : null,
                                                    )}
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>

                    {!isReadOnly && (
                        <TooltipProvider delayDuration={0}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            trackAction('custom_section_add');
                                            handleAddCustomSection();
                                        }}
                                        aria-disabled={isCustomSectionBlocked}
                                        className={cn(
                                            'relative mb-2 w-full rounded-lg border border-dashed border-border bg-surface p-3 text-muted-foreground transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary',
                                            isCustomSectionBlocked &&
                                                'cursor-default hover:border-border hover:bg-surface hover:text-muted-foreground',
                                        )}
                                        aria-label="Adicionar seção personalizada"
                                    >
                                        <span className="flex h-8 items-center justify-center">
                                            <Add className="size-5" />
                                        </span>
                                        {isCustomSectionBlocked && customSectionPlan ? (
                                            <span className="absolute right-3 top-1/2 inline-flex -translate-y-1/2 rounded-full border border-brand/15 bg-brand-subtle px-2.5 py-1 text-[0.7rem] font-semibold leading-none text-brand">
                                                Plano+
                                            </span>
                                        ) : null}
                                    </button>
                                </TooltipTrigger>
                                {isCustomSectionBlocked ? (
                                    <TooltipContent
                                        side="right"
                                        className="max-w-56 text-center leading-5"
                                    >
                                        {customSectionTooltip}
                                    </TooltipContent>
                                ) : null}
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            </div>
        </div>
    );
};
